import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

import AssetsLoader from '../utils/assetsLoader';
import { CARD_HEIGHT, CARD_WIDTH, RESOLUTION, APP_HEIGHT } from '../utils/commonUtils';

import CardContainer from './cardContainer';

export default class HandContainer extends PIXI.Container {
    constructor(handSize, handCards) {
        super();

        for (let i = 0; i < handSize; i++) {
            const cardContainer = new CardContainer(handCards?.[i]);
            cardContainer.hidePowerText();

            const location = this.calculateCardPosition(handSize, i);
            cardContainer.position.x = location.x;
            cardContainer.position.y = location.y;
            cardContainer.rotation = this.calculateCardRotation(handSize, i);

            this.addChild(cardContainer);
        }
    }

    calculateCardPosition(handSize, index) {
        const middleIndex = (handSize - 1) / 2;
        const offsetX = (index - middleIndex) * (CARD_WIDTH) * 0.65;
        const offsetY = Math.abs(middleIndex - index) * Math.abs(middleIndex - index) * (CARD_HEIGHT) * 0.03;
        return { x: offsetX, y: offsetY + (CARD_HEIGHT * 0.1) + 25*RESOLUTION };
    }

    calculateCardRotation(handSize, index) {
        const middleIndex = (handSize - 1) / 2;
        return (middleIndex - index) * -0.09;
    }

    updateHand(gameState, playerState, clientGameState) {
        let newHandSize = playerState.handCount;
        if (gameState.pendingAction?.type === 'TRIGGER_STEP' && gameState.pendingAction.triggeringPlayerId === playerState.playerId) {
            newHandSize--;
        }

        // destroy all previous cards
        while (this.children.length !== 0) {
            this.children[0].destroy({children: true});
        }

        // create new cards
        for (let i = 0; i < newHandSize; i++) {
            const cardContainer = new CardContainer(playerState.hand?.[i]);
            cardContainer.hidePowerText();
            
            const middleIndex = (newHandSize - 1) / 2;
            const offsetX = (i - middleIndex) * (CARD_WIDTH) * 0.65;
            const offsetY = Math.abs(middleIndex - i) * Math.abs(middleIndex - i) * (CARD_HEIGHT) * 0.03;

            cardContainer.x = offsetX;
            cardContainer.y = offsetY + (CARD_HEIGHT * 0.1) + 25*RESOLUTION;
            cardContainer.rotation = (middleIndex - i) * -0.09;

            const isPlayable = clientGameState?.playableCards?.includes(cardContainer.cardInstance?.instanceId);
            const isSelected = clientGameState?.selectedCard === cardContainer.cardInstance?.instanceId;
            const isSelectable = clientGameState?.selectableCards?.includes(cardContainer.cardInstance?.instanceId);
            if (isPlayable) {
                cardContainer.setHighlightPlayable();
            }
            
            if (isPlayable && isSelected) {
                cardContainer.setHighlightSelected();
            }

            if (isSelectable) {
                cardContainer.setHighlightSelectable();
            }

            this.addChild(cardContainer);
        }

        if (clientGameState?.selectedCard) {
            for (let i = 0; i < this.children.length; i++) {
                const cardContainer = this.children[i];
                if (clientGameState?.selectedCard === cardContainer.cardInstance?.instanceId) {
                    cardContainer.zIndex = 100;
                    cardContainer.y = -25*RESOLUTION;
                    cardContainer.rotation = 0;
                }
            }
        }
    }

    unhighlightAllCards() {
        this.children.forEach(child => child.unhighlightCard());
    }

    async playPlayerPlayedCharacterAnimation(instanceId, numOfCharacters, fieldIndex) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }

        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === instanceId);
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const powerText = cardContainer.getChildByLabel('powerTextContainer', true);
        powerText.visible = true;
        powerText.alpha = 0;

        const middleIndex = (numOfCharacters - 1) / 2;
        const padding = 0.1;
        const offsetX = (fieldIndex - middleIndex) * (CARD_WIDTH) * (1 + padding);

        const timeline = gsap.timeline();
        timeline.to(cardContainer, {
            duration:0.7,
            ease: CustomEase.create("custom", "M0,0 C0,0 0.515,-0.949 0.765,-0.699 1.015,-0.449 1,1 1,1 "),
            pixi: {
                scaleX: 0.83,
                scaleY: 0.83,
            }
        }).to(powerText, {
            duration: 0.7,
            ease: 'power2.in',
            pixi: {
                alpha: 1,
            }
        }, '<').to(cardContainer, {
            pixi: {
                y: -684*RESOLUTION,
                x: offsetX,
                rotation: 0,
            },
            duration: 0.5,
        }, "<").call(() => {
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CHARACTER_PLAYED_SOUND)?.play();
        }, []).call(() => {}, [], '+=0.30');

        await Promise.all([moveHandCardsTimeline, timeline]);

        if (cardContainer.cardInstance?.isBlocker) {
            const blockerFrame = cardContainer.getChildByLabel('blockerFrame', true);
            const blockerIcon = cardContainer.getChildByLabel('blockerIcon', true);

            blockerFrame.visible = true;
            blockerFrame.x = 0;
            blockerFrame.y = 0;
            blockerFrame.scale.set(1.5);
            blockerFrame.alpha = 0;
            
            // blockerIcon.visible = true;
            // blockerIcon.x = 0;
            // blockerIcon.y = 140;
            // blockerIcon.scale.set(1.5);
            // blockerIcon.alpha = 0;

            await timeline.to(blockerFrame, {
                duration: 0.10,
                ease: 'power1.out',
                pixi: {
                    scale: 1,
                    alpha: 1,
                }
            }, '+=0.15').call(() => {
                AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME_CLOSE_SOUND)?.play();
            }, [], '<');
        }
    }

    async playOpponentPlayedCharacterAnimation(cardInstance, numOfCharacters, fieldIndex) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === cardInstance.instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }

        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === cardInstance.instanceId);
        cardContainer.updateCardInstance(cardInstance);
        cardContainer.showBackFace();
        cardContainer.hidePowerText();
        cardContainer.hideSummoningSickness();
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const cardVisualsContainer = cardContainer.getChildByLabel('cardVisualsContainer');
        const blockerFrame = cardVisualsContainer.getChildByLabel('blockerFrame');
        blockerFrame.visible = false;

        const powerText = cardContainer.getChildByLabel('powerTextContainer');
        powerText.angle = 180;
        powerText.pivot.set(0, -CARD_HEIGHT - (powerText.y*2));
        powerText.visible = true;
        powerText.alpha = 0;

        const middleIndex = (numOfCharacters - 1) / 2;
        const padding = 0.1;
        const offsetX = -(fieldIndex - middleIndex) * (CARD_WIDTH) * (1 + padding);

        const putTimeline = gsap.timeline();
        putTimeline.to(cardContainer, {
            duration:0.7,
            ease: CustomEase.create("custom", "M0,0 C0,0 0.515,-0.949 0.765,-0.699 1.015,-0.449 1,1 1,1 "),
            pixi: {
                scaleX: 0.83,
                scaleY: 0.83,
            }
        }).to(cardContainer, {
            pixi: {
                y: -678*RESOLUTION,
                x: offsetX,
                rotation: 0,
            },
            duration: 0.5,
        }, "<").call(() => {
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CHARACTER_PLAYED_SOUND)?.play();
        }, []);

        const flipTimeline = gsap.timeline().timeScale(3);
        flipTimeline.to(cardVisualsContainer, {
            duration: 0.7,
            ease: 'power3.in',
            pixi: {
                scaleX: 0,
                angle: 90,
            }
        }).call(() => {
            cardContainer.showFrontFace();
        }).to(cardVisualsContainer, {
            duration: 0.7,
            ease: 'power3.out',
            pixi: {
                scaleX: 1,
                angle: 180,
            }
        }).to(powerText, {
            duration: 0.7,
            ease: 'power2.in',
            pixi: {
                alpha: 1,
            }
        });

        await Promise.all([moveHandCardsTimeline, putTimeline, flipTimeline]);

        if (cardContainer.cardInstance?.isBlocker) {
            const blockerFrame = cardContainer.getChildByLabel('blockerFrame', true);

            blockerFrame.visible = true;
            blockerFrame.x = 0;
            blockerFrame.y = 0;
            blockerFrame.scale.set(1.5);
            blockerFrame.alpha = 0;

            await putTimeline.to(blockerFrame, {
                duration: 0.10,
                ease: 'power1.out',
                pixi: {
                    scale: 1,
                    alpha: 1,
                }
            }, '+=0.45').call(() => {
                AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME_CLOSE_SOUND)?.play();
            }, [], '<');
        }
    }

    async playPlayerAddCardToHandAnimation(cardInstance) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0; i < this.children.length; i++) {
            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length+1, i);
            const rotation = this.calculateCardRotation(this.children.length+1, i);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.in',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
        }

        const newCardContainer = new CardContainer(cardInstance);
        newCardContainer.hidePowerText();
        newCardContainer.scale.set(1.7);
        newCardContainer.x = 0;
        newCardContainer.y = 1500 - APP_HEIGHT + (CARD_HEIGHT * 1.7 * 0.5);
        newCardContainer.angle = 0;
        this.addChild(newCardContainer);

        const moveNewCardTimeline = gsap.timeline();
        const position = this.calculateCardPosition(this.children.length, this.children.length-1);
        const rotation = this.calculateCardRotation(this.children.length, this.children.length-1);
        moveNewCardTimeline.to(newCardContainer, {
            duration: 0.7,
            ease: 'power3.in',
            pixi: {
                x: position.x,
                y: position.y,
                angle: rotation * (180 / Math.PI),
                scale: 1,
            }
        });

        await Promise.all([moveHandCardsTimeline, moveNewCardTimeline]);
    }

    async playMoveHandCardsOnePosition() {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0; i < this.children.length; i++) {
            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length+1, i);
            const rotation = this.calculateCardRotation(this.children.length+1, i);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.in',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
        }

        return moveHandCardsTimeline;
    }

    async playPlayerPlayedStageAnimation(instanceId) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }
        
        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === instanceId);
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const destPosition = this.toLocal({ x: 832, y: 1913 });

        await gsap.timeline().to(cardContainer, {
            duration: 0.7,
            ease: 'power3.inOut',
            pixi: {
                x: destPosition.x,
                y: destPosition.y,
                scale: 0.5,
            }
        });
    }
}
